import { z } from 'zod';
import { clientTypeSchema } from './client-profile';
/**
 * DTO для раздела «Все клиенты» в кабинете staff (MANAGER / ADMIN / AUDITOR).
 *
 * Краткий список — основные идентификаторы для таблицы.
 * Карточка — те же поля + история (поручения, договоры, перевозки, платежи).
 */
/** Краткая инфа о менеджере для отображения в списке/карточке клиента. */
export const responsibleManagerSchema = z.object({
    id: z.string(),
    email: z.string(),
    fullName: z.string().nullable(),
});
export const managerClientListItemSchema = z.object({
    id: z.string(),
    serviceId: z.string(),
    type: clientTypeSchema,
    displayName: z.string(),
    /** Главный идентификатор: ИНН для всех типов кроме ФЛ. */
    primaryId: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    actualAddress: z.string().nullable(),
    edoSubscriberId: z.string().nullable(),
    isActive: z.boolean(),
    deregisteredAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    /** Сумма-сводка для UI. */
    ordersTotal: z.number().int().nonnegative(),
    shipmentsActive: z.number().int().nonnegative(),
    /** Ответственный менеджер — null если не закреплён. */
    primaryManager: responsibleManagerSchema.nullable(),
});
export const managerClientDetailSchema = managerClientListItemSchema.extend({
    /** type-specific блок. Зависит от type, парсится клиентом. */
    details: z.record(z.unknown()).nullable(),
    /** Краткая история: последние 20 поручений. */
    recentOrders: z.array(z.object({
        id: z.string(),
        number: z.string(),
        status: z.string(),
        cargoName: z.string(),
        cargoWeightKg: z.number(),
        createdAt: z.string().datetime(),
        submittedAt: z.string().datetime().nullable(),
    })),
    /** Все договоры клиента. */
    contracts: z.array(z.object({
        id: z.string(),
        number: z.string(),
        orderNumber: z.string(),
        signedAt: z.string().datetime(),
        endedAt: z.string().datetime().nullable(),
        shipmentStatus: z.string().nullable(),
    })),
    /** Финансовая сводка по платежам. */
    paymentsSummary: z.array(z.object({
        currency: z.string(),
        totalAmount: z.number(),
        paymentsCount: z.number().int().nonnegative(),
    })),
});
export const managerClientsQuerySchema = z.object({
    /** Поиск по displayName / inn / serviceId. */
    search: z.string().max(200).optional(),
    /** Включать прекращённых. По умолчанию — нет. */
    includeDeregistered: z
        .union([z.boolean(), z.literal('true'), z.literal('false')])
        .optional()
        .transform((v) => v === true || v === 'true')
        .pipe(z.boolean().default(false)),
    /** Только закреплённые за текущим пользователем. По умолчанию — нет. */
    mineOnly: z
        .union([z.boolean(), z.literal('true'), z.literal('false')])
        .optional()
        .transform((v) => v === true || v === 'true')
        .pipe(z.boolean().default(false)),
});
/** Тело PATCH /manager/clients/:id/responsible-manager. */
export const setResponsibleManagerSchema = z.object({
    /** null = снять (клиент становится без ответственного). */
    managerId: z.string().nullable(),
});
/** DTO для дропдауна «выбрать менеджера» в админ-UI. */
export const managerOptionSchema = z.object({
    id: z.string(),
    email: z.string(),
    fullName: z.string().nullable(),
});
/** Активный или исторический grant доступа менеджеру к чужому клиенту. */
export const clientAccessDtoSchema = z.object({
    id: z.string(),
    managerId: z.string(),
    managerEmail: z.string(),
    managerFullName: z.string().nullable(),
    grantedByEmail: z.string(),
    reason: z.string().nullable(),
    createdAt: z.string().datetime(),
    expiresAt: z.string().datetime().nullable(),
    revokedAt: z.string().datetime().nullable(),
    active: z.boolean(),
});
export const grantClientAccessSchema = z.object({
    managerId: z.string(),
    /** ISO-дата истечения. null/undefined = бессрочный. */
    expiresAt: z
        .preprocess((v) => {
        if (v === null || v === undefined)
            return null;
        if (typeof v !== 'string')
            return v;
        const t = v.trim();
        return t === '' ? null : t;
    }, z.string().datetime({ offset: true }).nullable())
        .optional()
        .default(null),
    reason: z
        .preprocess((v) => {
        if (v === null || v === undefined)
            return null;
        if (typeof v !== 'string')
            return v;
        const t = v.trim();
        return t === '' ? null : t;
    }, z.string().max(500).nullable())
        .optional()
        .default(null),
});
//# sourceMappingURL=manager-clients.js.map